/**
 * ReportsPage — orchestrator.
 * All state/logic is in hooks (Stage 2). All UI is in sub-components (Stage 3).
 *
 * Hooks:
 *   useReportFilters     — filter state, cascading sale data, runReport
 *   useSavedReports      — saved/draft/approved list, bulk actions
 *   useReportFieldPrefs  — field selection, localStorage prefs
 *
 * Components:
 *   ReportSelector       — report type picker cards
 *   ReportFiltersBar     — per-report filter controls + Run / Approve buttons
 *   ReportSummaryCards   — aggregated totals per report type
 *   ReportResultsTable   — generic tabular results (non-DSU)
 *   DraftReportsCard     — DSU draft reports pending approval
 *   DraftPreviewSection  — inline DSU draft preview with Approve / Rerun
 *   ApprovedReportsTable — all approved saved reports
 *   CustomizeFieldsDialog — field column selector dialog
 *   DailySaleUpdateDocument — DSU document layout (unchanged)
 *   CustomReportBuilder  — custom report builder (unchanged)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Save } from 'lucide-react';
import SidebarLayout from '../../components/layout/SidebarLayout';
import api from '../../services/api';
import { exportToCSV } from '../../utils/exportCSV';
import DailySaleUpdateDocument from '../../components/reports/DailySaleUpdateDocument';
import CustomReportBuilder from '../../components/reports/CustomReportBuilder';
import DepartureFormBuilder from '../../components/reports/DepartureFormBuilder';
import DepartureFormDocument from '../../components/reports/DepartureFormDocument';
import ReportSelector from '../../components/reports/ReportSelector';
import ReportFiltersBar from '../../components/reports/ReportFiltersBar';
import ReportSummaryCards from '../../components/reports/ReportSummaryCards';
import ReportResultsTable from '../../components/reports/ReportResultsTable';
import DraftReportsCard from '../../components/reports/DraftReportsCard';
import DraftPreviewSection from '../../components/reports/DraftPreviewSection';
import ApprovedReportsTable from '../../components/reports/ApprovedReportsTable';
import CustomizeFieldsDialog from '../../components/reports/CustomizeFieldsDialog';

import { fmt, REPORTS, REPORT_FIELDS } from '../../hooks/reports/reportConstants';
import { useReportFilters } from '../../hooks/reports/useReportFilters';
import { useSavedReports } from '../../hooks/reports/useSavedReports';
import { useReportFieldPrefs } from '../../hooks/reports/useReportFieldPrefs';

const ReportsPage = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [activeReport, setActiveReport] = useState(null);
  const [saving, setSaving] = useState(false);
  const [departureData, setDepartureData] = useState(null);
  const [sectionOrder, setSectionOrder] = useState(['vetting', 'views']);
  const [isFarm, setIsFarm] = useState(false);

  useEffect(() => {
    if (currentUser?.tenant_id) {
      api.get(`/tenants/${currentUser.tenant_id}`)
        .then(r => setIsFarm(r.data?.is_farm === true))
        .catch(() => {});
    }
  }, [currentUser?.tenant_id]);

  // ── Hooks ──────────────────────────────────────────────────────────────────
  const {
    data, setData,
    loading,
    filters, setFilters,
    sales, accounts,
    saleFilterData, loadingFilterData,
    runReport, canRunReport,
  } = useReportFilters(activeReport);

  const {
    savedReports,
    loadingSaved,
    savedSearch, setSavedSearch,
    fetchSavedReports,
    selectedReportIds, setSelectedReportIds,
    toggleSelectReport,
    selectedDrafts,
    selectedApproved,
    handleBulkApprove,
    handleBulkDelete,
    bulkSending,
    handleBulkSend,
    draftSearch, setDraftSearch,
    previewDraft, setPreviewDraft,
  } = useSavedReports();

  const {
    selectedFields,
    fieldDialogOpen, setFieldDialogOpen,
    saveFieldPrefs,
    allFieldsForReport,
    toggleField,
  } = useReportFieldPrefs(activeReport);

  // React Query manages the initial fetch in useSavedReports — no manual trigger needed

  // ── Render helpers ─────────────────────────────────────────────────────────
  const getRows = () => {
    if (!data) return [];
    if (activeReport === 'daily-sale-updates')      return data.rows || [];
    if (activeReport === 'tenant-financials')        return data.accounts || [];
    if (activeReport === 'net-proceeds')             return data.entries || [];
    if (activeReport === 'outstanding-receivables')  return data.invoices || [];
    return Array.isArray(data) ? data : [];
  };

  const getVisibleColumns = () => allFieldsForReport().filter(f => selectedFields.includes(f.key));

  const renderCell = (row, field) => {
    const val = row[field.key];
    if (val === null || val === undefined || val === '') return '-';
    if (field.key === 'bucket') {
      const colors = { current: 'bg-gray-100 text-gray-700', '30': 'bg-yellow-100 text-yellow-700', '60': 'bg-orange-100 text-orange-700', '90+': 'bg-red-100 text-red-700' };
      return <Badge className={`text-xs pointer-events-none ${colors[val] || ''}`}>{val === 'current' ? 'Current' : `${val} Days`}</Badge>;
    }
    if (field.key === 'sold_rate') return <Badge className={`text-xs pointer-events-none ${val > 70 ? 'bg-green-100 text-green-700' : val > 40 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>{val}%</Badge>;
    if (field.key === 'stage') return <Badge variant="outline" className="text-xs capitalize pointer-events-none">{String(val).replace(/_/g, ' ')}</Badge>;
    if (field.key === 'result' && val === 'sold') return <Badge className="bg-green-100 text-green-700 pointer-events-none">Sold</Badge>;
    if (field.key === 'rating') return <Badge variant="outline" className="text-xs pointer-events-none">{val || '-'}</Badge>;
    if (['sale_price', 'sale_company_commission', 'commission', 'entry_fee', 'other_fees', 'total_fees', 'net_proceeds', 'owner_net_proceeds', 'amount',
      'all_time_commissions', 'ytd_commissions', 'all_time_revenue', 'ytd_revenue', 'avg_price', 'median_price', 'total_revenue', 'reserve_price'].includes(field.key)) {
      const color = ['net_proceeds', 'owner_net_proceeds'].includes(field.key) ? 'text-primary font-bold' : field.key === 'total_fees' ? 'text-red-600' : 'font-medium';
      return <span className={color}>{fmt(val)}</span>;
    }
    if (['due_date', 'invoice_date', 'session_date'].includes(field.key) && val) return new Date(val).toLocaleDateString();
    if (field.key === 'hip_number') return `#${val || 'TBD'}`;
    if (field.key === 'liked' || field.key === 'viewed_vet_report') {
      if (typeof val === 'number') return val > 0 ? <Badge className="bg-green-100 text-green-700 pointer-events-none">{val}</Badge> : <span className="text-muted-foreground">0</span>;
      return val ? <Badge className="bg-green-100 text-green-700 pointer-events-none">Yes</Badge> : <span className="text-muted-foreground">No</span>;
    }
    if (field.key === 'funnel_insights') return <span className="text-xs text-muted-foreground">{val}</span>;
    if (field.key === 'owner_percentage') return <span className="text-xs">{val}</span>;
    if (field.key === 'horse_sex' || field.key === 'horse_type' || field.key === 'horse_cover_status') return <span className="capitalize">{val}</span>;
    if (field.key === 'horse_vices' && val) return <span className="text-xs text-orange-600">{val}</span>;
    return String(val);
  };

  const handleDownload = () => {
    const cols = getVisibleColumns();
    exportToCSV(getRows(), cols.map(f => ({ key: f.key, label: f.label, accessor: r => r[f.key] })), `${activeReport}-report`);
  };

  const handlePublish = async () => {
    if (!data || !activeReport) return;
    try {
      const reportDef = REPORTS.find(r => r.id === activeReport);
      await api.post('/reports/publish-to-portal', {
        report_type: activeReport,
        report_name: reportDef?.label || activeReport,
        report_data: data,
        crm_entity_ids: filters.crm_entity_id ? [filters.crm_entity_id] : [],
      });
      toast.success('Report published to Owner Portal');
    } catch { toast.error('Failed to publish'); }
  };

  const handleSaveReport = async () => {
    if (!data || !activeReport) return;
    setSaving(true);
    try {
      const reportDef = REPORTS.find(r => r.id === activeReport);
      const cols = getVisibleColumns().map(f => ({ key: f.key, label: f.label }));
      let summaryData = null;
      if (activeReport === 'tenant-financials') summaryData = { 'All-Time Commissions': data.total_all_time, 'YTD Commissions': data.total_ytd, 'All-Time Gross Sales': data.total_all_time_revenue, 'YTD Gross Sales': data.total_ytd_revenue };
      else if (activeReport === 'net-proceeds') summaryData = {
        'Gross Sales': data.total_sales,
        'Total Net Proceeds': data.total_net_proceeds,
        ...(data.total_consignor_commissions != null ? { 'Total Consignor Commissions': data.total_consignor_commissions } : {}),
        ...(data.total_owner_net_proceeds != null ? { "Owner's Net Proceeds": data.total_owner_net_proceeds } : {}),
      };
      else if (activeReport === 'outstanding-receivables') summaryData = data.totals;
      const selectedEntity = accounts.find(a => a.id === filters.crm_entity_id);
      const isAllAccounts = filters.crm_entity_id === '__all_accounts__';
      const res = await api.post('/reports/save', {
        report_type: activeReport,
        report_name: activeReport === 'daily-sale-updates'
          ? isAllAccounts
            ? `Daily Update (${data?.sale_name || 'Sale'}) - ALL`
            : filters.opportunity_id === '__all__'
              ? `Daily Update (${data?.sale_name || 'Sale'}) - All Horses`
              : `Daily Update (${data?.sale_name || 'Sale'})${data?.rows?.[0]?.horse_name ? ` - ${data.rows[0].horse_name}` : ''}`
          : (reportDef?.label || activeReport),
        report_data: data,
        summary_data: summaryData,
        columns: cols,
        filters,
        crm_entity_id: isAllAccounts ? null : (filters.crm_entity_id || null),
        crm_entity_name: isAllAccounts ? 'ALL' : (selectedEntity?.system_name || ''),
        row_count: rows.length,
        report_date: new Date().toLocaleDateString('en-CA'),
        section_order: activeReport === 'daily-sale-updates' ? sectionOrder : null,
      });
      toast.success('Report saved!');
      navigate(`/reports/saved/${res.data.id}`);
    } catch { toast.error('Failed to save report'); }
    setSaving(false);
  };

  const rows = getRows();
  const visibleCols = getVisibleColumns();

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <SidebarLayout>
      <div className="space-y-4">
        <div>
          <h1 className="font-serif text-3xl font-bold">Reports</h1>
          <p className="text-muted-foreground mt-1">Generate and export comprehensive reports</p>
        </div>

        <ReportSelector
          activeReport={activeReport}
          onSelect={(id) => { setActiveReport(id); setData(null); setPreviewDraft(null); setDepartureData(null); }}
          isFarm={isFarm}
        />

        {/* ReportFiltersBar — hidden for departure-form (has its own form) */}
        {activeReport !== 'departure-form' && (
        <ReportFiltersBar
          activeReport={activeReport}
          filters={filters} setFilters={setFilters}
          sales={sales} accounts={accounts}
          saleFilterData={saleFilterData} loadingFilterData={loadingFilterData}
          loading={loading} canRunReport={canRunReport}
          onRun={() => runReport(activeReport, selectedFields)}
          data={data} saving={saving} onSave={handleSaveReport}
          onCustomizeFields={() => setFieldDialogOpen(true)}
        />
        )}

        <ReportSummaryCards activeReport={activeReport} data={data} visibleCols={visibleCols} />

        {activeReport === 'custom' && <CustomReportBuilder onApprove={fetchSavedReports} />}

        {activeReport === 'departure-form' && !departureData && (
          <DepartureFormBuilder
            onGenerated={(d) => setDepartureData(d)}
          />
        )}

        {activeReport === 'departure-form' && departureData && (
          <div className="space-y-3">
            {/* Action bar — exact same layout as DraftPreviewSection */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div />
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setDepartureData(null)}>
                  Rerun Report
                </Button>
                <Button
                  className="bg-primary hover:bg-primary/90 text-white"
                  disabled={saving}
                  onClick={async () => {
                    setSaving(true);
                    try {
                      const horse = departureData.horse || {};
                      const travel = departureData.travel || {};
                      // Format: "Departure Form - Horse Name - Direction → Destination (Jun 30, 2026 @ 8:00 PM)"
                      const dir = travel.travel_direction === 'from_farm' ? 'On Farm' : travel.travel_direction === 'to_farm' ? 'Returning' : 'Travel';
                      const dest = travel.travel_destination_name || travel.travel_dest_other || '';
                      const rawDate = travel.start_date || travel.next_due_date || '';
                      const rawTime = travel.preferred_time || '';
                      let dateLabel = '';
                      if (rawDate) {
                        try { dateLabel = new Date(rawDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { dateLabel = rawDate; }
                      }
                      let timeLabel = '';
                      if (rawTime) {
                        try { const [h, m] = rawTime.split(':').map(Number); timeLabel = ` @ ${h % 12 || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`; } catch {}
                      }
                      const travelPart = `${dir}${dest ? ` → ${dest}` : ''}${dateLabel ? ` (${dateLabel}${timeLabel})` : ''}`;
                      const horseName = horse.system_name || horse.name || 'Horse';
                      const name = `Departure Form - ${horseName} - ${travelPart}`;
                      const saveRes = await api.post('/reports/save', {
                        report_type: 'departure-form',
                        report_name: name,
                        columns: [],
                        report_data: departureData,
                      });
                      await api.post('/dsu-automation/bulk-approve', { report_ids: [saveRes.data.id] });
                      navigate(`/reports/saved/${saveRes.data.id}`);
                    } catch { toast.error('Failed to save report'); }
                    setSaving(false);
                  }}>
                  <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Approve Report'}
                </Button>
              </div>
            </div>
            <DepartureFormDocument data={departureData} />
          </div>
        )}

        {data && activeReport === 'daily-sale-updates' && (
          <DailySaleUpdateDocument data={data} columns={visibleCols} sectionOrder={sectionOrder} />
        )}

        <ReportResultsTable
          activeReport={activeReport}
          data={data} rows={rows} visibleCols={visibleCols}
          allFieldsForReport={allFieldsForReport}
          filters={filters}
          renderCell={renderCell}
        />

        {previewDraft && activeReport !== 'departure-form' && (
          <DraftPreviewSection
            previewDraft={previewDraft}
            onClose={() => setPreviewDraft(null)}
            visibleCols={visibleCols}
            fetchSavedReports={fetchSavedReports}
            navigate={navigate}
            setPreviewDraft={setPreviewDraft}
          />
        )}

        {/* Departure form saved drafts */}
        {activeReport === 'departure-form' && (() => {
          const dfDrafts = savedReports.filter(r => r.report_type === 'departure-form' && r.report_status === 'draft');
          if (!dfDrafts.length) return null;
          return (
            <div className="space-y-2">
              <p className="text-sm font-semibold">Saved Drafts</p>
              {dfDrafts.map(r => (
                <div key={r.id} className="flex items-center justify-between p-3 border rounded hover:bg-muted/20 cursor-pointer"
                  onClick={() => navigate(`/reports/saved/${r.id}`)}>
                  <div>
                    <p className="text-sm font-medium">{r.report_name}</p>
                    <p className="text-xs text-muted-foreground">{r.saved_at ? new Date(r.saved_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : ''}</p>
                  </div>
                  <span className="text-xs bg-yellow-100 text-yellow-800 border border-yellow-200 px-2 py-0.5 rounded">Draft</span>
                </div>
              ))}
            </div>
          );
        })()}

        {/* DSU Drafts — only for DSU report type */}
        {activeReport !== 'departure-form' && (
        <DraftReportsCard
          activeReport={activeReport}
          savedReports={savedReports} loadingSaved={loadingSaved}
          draftSearch={draftSearch} setDraftSearch={setDraftSearch}
          selectedReportIds={selectedReportIds} setSelectedReportIds={setSelectedReportIds}
          toggleSelectReport={toggleSelectReport}
          handleBulkApprove={handleBulkApprove}
          handleBulkDelete={handleBulkDelete}
          onPreview={setPreviewDraft}
        />
        )}

        <ApprovedReportsTable
          activeReport={activeReport}
          savedReports={savedReports} loadingSaved={loadingSaved}
          savedSearch={savedSearch} setSavedSearch={setSavedSearch}
          selectedReportIds={selectedReportIds} setSelectedReportIds={setSelectedReportIds}
          toggleSelectReport={toggleSelectReport}
          selectedApproved={selectedApproved}
          bulkSending={bulkSending} handleBulkSend={handleBulkSend}
          navigate={navigate}
        />

        <CustomizeFieldsDialog
          open={fieldDialogOpen} onOpenChange={setFieldDialogOpen}
          activeReport={activeReport}
          selectedFields={selectedFields}
          toggleField={toggleField}
          saveFieldPrefs={saveFieldPrefs}
          sectionOrder={sectionOrder}
          setSectionOrder={setSectionOrder}
          filters={filters}
          setFilters={setFilters}
        />
      </div>
    </SidebarLayout>
  );
};

export default ReportsPage;
